class BulkQuantitySelector extends HTMLElement {
  constructor() {
    super();
    this.options = this.querySelectorAll('.bulk-quantity-option');
    this.selectedBulkVariantId = null;
    this.selectedBulkProductId = null;
    this.variantChangeUnsubscriber = undefined;
    this.setStoreFromBody();
    this.init();
    this.setupAddToCartListener();
    this.setupVariantAvailabilityCheck();
  }

  setStoreFromBody() {
    const body = document.body;
    let store = 'freedom'; 
    let storeColor = '#797e65'; 
    
    if (body.classList.contains('store-lax')) {
      store = 'lax';
      storeColor = '#797e65'; 
    } else if (body.classList.contains('store-xtreme')) {
      store = 'xtreme';
      storeColor = '#A61B1B'; 
    } else if (body.classList.contains('store-ammo-load')) {
      store = 'ammo-load';
      storeColor = '#285287'; 
    }
    
    this.setAttribute('data-store', store);
    this.style.setProperty('--bulk-selector-primary-color', storeColor);
  }

  init() {
    this.selectFirstOption();

    this.options.forEach((option) => {
      const radio = option.querySelector('input[type="radio"]');
      option.addEventListener('click', (e) => {
        e.preventDefault();
        this.handleOptionSelect(option, radio);
      });

      option.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          this.handleOptionSelect(option, radio);
        }
      });

      if (radio) {
        radio.addEventListener('change', () => {
          if (radio.checked) {
            this.updateActiveState(option);
            this.selectedBulkVariantId = option.dataset.variantId;
            this.selectedBulkProductId = option.dataset.productId;
            this.updatePriceDisplay(option);
          }
        });
      }
    });

    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        this.options = this.querySelectorAll('.bulk-quantity-option');
        this.selectFirstOption();
        this.checkVariantAvailability();
      }
    });

    if (typeof subscribe !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        if (event.data && event.data.variant) {
          this.updateAvailabilityForVariant(event.data.variant.id, event.data.variant.available);
        }
      });
    }
  }

  selectFirstOption() {
    this.options = this.querySelectorAll('.bulk-quantity-option');
    
    if (this.options.length === 0) return;

    const radioName = this.options[0].querySelector('input[type="radio"]')?.name;
    
    if (radioName) {
      document.querySelectorAll(`input[type="radio"][name="${radioName}"]`).forEach(radio => {
        radio.removeAttribute('checked');
        radio.checked = false;
      });
    }
    
    this.options.forEach(option => {
      option.classList.remove('bulk-quantity-option--active');
    });

    const currentProductId = this.currentProductId;
    const currentProductOption = Array.from(this.options).find(option => {
      return option.dataset.productId === currentProductId;
    });

    const firstOption = currentProductOption || this.options[0];

    if (firstOption) {
      const radio = firstOption.querySelector('input[type="radio"]');
      if (radio) {
        radio.checked = true;
        radio.setAttribute('checked', 'checked');
        radio.removeAttribute('disabled');
        this.updateActiveState(firstOption);
        this.selectedBulkVariantId = firstOption.dataset.variantId;
        this.selectedBulkProductId = firstOption.dataset.productId;
        this.updatePriceDisplay(firstOption);
      }
    }
  }

  handleOptionSelect(option, radio) {
    const { productId, variantId, productUrl } = option.dataset;
    if (!variantId || !productUrl) return;

    if (productId === this.currentProductId) {
      if (variantId) {
        const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
        if (variantInput) {
          variantInput.checked = true;
          variantInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
      if (radio) {
        radio.checked = true;
        radio.removeAttribute('disabled');
      }
      this.updateActiveState(option);
      this.selectedBulkVariantId = variantId;
      this.selectedBulkProductId = productId;
      this.updatePriceDisplay(option);
      return;
    }

    if (radio) {
      radio.checked = true;
      radio.removeAttribute('disabled');
    }
    this.updateActiveState(option);
    this.selectedBulkVariantId = variantId;
    this.selectedBulkProductId = productId;

    const url = new URL(productUrl, window.location.origin);
    url.searchParams.set('variant', variantId);
    
    const currentStore = this.getAttribute('data-store') || 'freedom';
    if (currentStore !== 'freedom') {
      url.searchParams.set('store', currentStore);
    }

    window.location.href = url.toString();
  }

  setupAddToCartListener() {
    const setupListener = () => {
      const productForm = document.querySelector('product-form');
      if (!productForm || !productForm.form) {
        setTimeout(setupListener, 100);
        return;
      }

      productForm.form.addEventListener('submit', (e) => {
        if (this.selectedBulkVariantId) {
          e.preventDefault();
          e.stopImmediatePropagation();
          
          this.addBulkProductToCart(this.selectedBulkVariantId);
          return false;
        }
      }, { capture: true, passive: false });
    };

    setTimeout(setupListener, 0);
  }

  async addBulkProductToCart(variantId) {
    if (this.addingToCart) return;
    this.addingToCart = true;

    const productForm = document.querySelector('product-form');
    const submitButton = productForm?.querySelector('[type="submit"]');
    const submitButtonText = submitButton?.querySelector('span');
    const loadingSpinner = productForm?.querySelector('.loading__spinner');

    if (submitButton) {
      submitButton.setAttribute('aria-disabled', 'true');
      submitButton.classList.add('loading');
      if (loadingSpinner) loadingSpinner.classList.remove('hidden');
    }

    let data = null;
    try {
      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      
      const requestBody = {
        items: [{
          id: parseInt(variantId, 10),
          quantity: 1
        }]
      };

      if (cart && cart.getSectionsToRender) {
        const sections = cart.getSectionsToRender().map((section) => section.id);
        if (sections.length > 0) {
          requestBody.sections = sections;
          requestBody.sections_url = window.location.pathname;
        }
        cart.setActiveElement(document.activeElement);
      }

      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        },
        body: JSON.stringify(requestBody)
      });

      data = await response.json();

      if (data.status) {
        console.error('Error adding bulk product to cart:', data.description || data.message);
        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartError, {
            source: 'bulk-quantity-selector',
            productVariantId: variantId,
            errors: data.errors || data.description,
            message: data.message,
          });
        }
        
        if (productForm && productForm.handleErrorMessage) {
          productForm.handleErrorMessage(data.description || data.message);
        }
      } else {
        if (cart && cart.renderContents) {
          cart.renderContents(data);
        }

        if (typeof publish !== 'undefined' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, {
            source: 'bulk-quantity-selector',
            productVariantId: variantId,
            cartData: data,
          });
        }

        this.clearSelection();
      }
    } catch (error) {
      console.error('Error adding bulk product to cart:', error);
      if (productForm && productForm.handleErrorMessage) {
        productForm.handleErrorMessage('An error occurred while adding the product to cart.');
      }
    } finally {
      this.addingToCart = false;
      
      if (submitButton) {
        submitButton.classList.remove('loading');
        if (loadingSpinner) loadingSpinner.classList.add('hidden');
        if (!data || !data.status) {
          submitButton.removeAttribute('aria-disabled');
        }
      }
    }
  }

  get currentVariantId() {
    const variantInput = document.querySelector('input[name="id"]');
    return variantInput ? variantInput.value : null;
  }

  updateActiveState(activeOption) {
    this.options.forEach((option) => {
      option.classList.remove('bulk-quantity-option--active');
      const radio = option.querySelector('input[type="radio"]');
      if (radio) radio.checked = option === activeOption;
    });
    activeOption?.classList.add('bulk-quantity-option--active');
  }

  clearSelection() {
    this.selectFirstOption();
  }

  setLoadingState(option, isLoading) {
    if (isLoading) {
      option.classList.add('bulk-quantity-option--loading');
      option.style.pointerEvents = 'none';
      this.options.forEach((opt) => {
        if (opt !== option) opt.style.opacity = '0.5';
      });
    } else {
      option.classList.remove('bulk-quantity-option--loading');
      option.style.pointerEvents = '';
      this.options.forEach((opt) => opt.style.opacity = '');
    }
  }

  get currentProductId() {
    return document.querySelector('product-info')?.dataset.productId || null;
  }

  setupVariantAvailabilityCheck() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        setTimeout(() => this.checkVariantAvailability(), 100);
      });
    } else {
      setTimeout(() => this.checkVariantAvailability(), 100);
    }
  }

  async checkVariantAvailability() {
    this.options.forEach(option => {
      const variantId = option.dataset.variantId;
      if (!variantId) return;
      const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
      if (variantInput) {
        const isAvailable = !variantInput.hasAttribute('disabled');
        this.updateOptionAvailability(option, isAvailable);
      } else {
        const productForm = document.querySelector('product-form');
        if (productForm && productForm.form) {
          const currentVariantInput = productForm.form.querySelector('input[name="id"]');
          if (currentVariantInput && currentVariantInput.value === variantId) {
            const isAvailable = !currentVariantInput.hasAttribute('disabled');
            this.updateOptionAvailability(option, isAvailable);
          }
        }
      }
    });

    const uncheckedOptions = Array.from(this.options).filter(option => {
      const variantId = option.dataset.variantId;
      if (!variantId) return false;
      const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
      return !variantInput;
    });

    if (uncheckedOptions.length > 0) {
      setTimeout(() => {
        uncheckedOptions.forEach(option => {
          const variantId = option.dataset.variantId;
          if (!variantId) return;
          const variantInput = document.querySelector(`input[name="id"][value="${variantId}"]`);
          if (variantInput) {
            const isAvailable = !variantInput.hasAttribute('disabled');
            this.updateOptionAvailability(option, isAvailable);
          }
        });
      }, 300);
    }
  }

  updateAvailabilityForVariant(variantId, isAvailable) {
    const option = Array.from(this.options).find(opt => opt.dataset.variantId === variantId?.toString());
    if (option) {
      this.updateOptionAvailability(option, isAvailable);
    }
  }

  updateOptionAvailability(option, isAvailable) {
    const radio = option.querySelector('input[type="radio"]');
    
    if (isAvailable) {
      option.classList.remove('bulk-quantity-option--unavailable');
      option.removeAttribute('aria-disabled');
      if (radio) {
        radio.removeAttribute('disabled');
      }
    } else {
      option.classList.add('bulk-quantity-option--unavailable');
      option.setAttribute('aria-disabled', 'true');
      if (radio && option.dataset.productId !== this.currentProductId) {
        radio.setAttribute('disabled', 'disabled');
      }
    }
  }

  disconnectedCallback() {
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }
  }

  formatMoney(cents, format) {
    if (typeof format === 'string' && format.includes('{{amount}}')) {
      const amount = (cents / 100).toFixed(2);
      return format.replace('{{amount}}', amount);
    }
    return '$' + (cents / 100).toFixed(2);
  }

  updatePriceDisplay(option) {
    const {
      price,
      compareAtPrice,
      priceFormatted,
      compareAtPriceFormatted,
      quantityCount,
      quantityType,
      pricePerRound
    } = option.dataset;

    if (!price) return;

    const priceBlock = document.querySelector('[data-la-dn-price]') || document.querySelector('.price-block');
    const priceElement = priceBlock ? priceBlock.querySelector('.price') : document.querySelector('.product__info-wrapper .price, .price--large');
    
    if (!priceElement) return;

    const priceCents = parseInt(price, 10);
    const compareAtPriceCents = parseInt(compareAtPrice || price, 10);
    const hasSale = compareAtPriceCents > priceCents;

    const moneyFormat = window.Shopify?.shop?.money_format || '${{amount}}';

    const regularPriceElement = priceElement.querySelector('.price__regular .price-item--regular');
    if (regularPriceElement) {
      regularPriceElement.textContent = priceFormatted || this.formatMoney(priceCents, moneyFormat);
    }

    const salePriceElement = priceElement.querySelector('.price__sale .price-item--sale');
    if (salePriceElement) {
      salePriceElement.textContent = priceFormatted || this.formatMoney(priceCents, moneyFormat);
    }

    const comparePriceElement = priceElement.querySelector('.price__sale .price-item--regular s');
    if (comparePriceElement && hasSale) {
      comparePriceElement.textContent = compareAtPriceFormatted || this.formatMoney(compareAtPriceCents, moneyFormat);
    }

    // Update price per round display
    const priceBox = priceElement.querySelector('.price__box.product-page');
    if (priceBox && quantityCount && quantityType) {
      const labelElement = priceBox.querySelector('.price__box--content__label');
      const valueElement = priceBox.querySelector('.price__box--content__value span:first-child');

      if (labelElement) {
        const quantityTypeCapitalized = quantityType.charAt(0).toUpperCase() + quantityType.slice(1);
        labelElement.textContent = `Per ${quantityTypeCapitalized} of ${quantityCount}`;
      }

      if (valueElement && pricePerRound) {
        valueElement.textContent = pricePerRound;
      }
    }

    const cardLabelElement = priceElement.querySelector('.price__box--content__label.card-p');
    const cardValueElement = priceElement.querySelector('.price__box--content__value.card-p span:first-child');

    if (cardLabelElement && quantityCount && quantityType) {
      const quantityTypeCapitalized = quantityType.charAt(0).toUpperCase() + quantityType.slice(1);
      cardLabelElement.textContent = `Per ${quantityTypeCapitalized} of ${quantityCount}`;
    }

    if (cardValueElement && pricePerRound) {
      cardValueElement.textContent = pricePerRound;
    }

    if (hasSale) {
      priceElement.classList.add('price--on-sale');
    } else {
      priceElement.classList.remove('price--on-sale');
    }
  }
}

customElements.define('bulk-quantity-selector', BulkQuantitySelector);